import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import {
  LayoutDashboard, CalendarDays, Building2, Map, BarChart3,
  CalendarCheck, ChevronLeft, ChevronRight, Menu, LogOut,
  Search, Smartphone, ArrowLeft, Shield, Users, QrCode,
  Layers, Bot, Headset, Home, UserCheck, Tag, TrendingUp, Settings, ScanLine, FileText,
} from "lucide-react";
import { AdminAuthProvider, useAdminAuth, getRoleLabel, getRoleBadgeColor, type AdminRole } from "./auth/AdminAuthContext";
import { useAdminEvent } from "./hooks/useAdminEvent";
import { AdminNotificationsBell } from "./components/AdminNotificationsBell";

// ─── Hooks ────────────────────────────────────────────────────────────────
function useCurrentEventId() {
  const { pathname } = useLocation();
  const m = pathname.match(/\/events\/([^/]+)/);
  return m ? m[1] : null;
}

function usePageLabel(role: AdminRole | undefined, currentEventId: string | null) {
  const { pathname } = useLocation();
  if (pathname.endsWith("/login")) return "Вход";
  if (role === "exhibitor") {
    if (pathname.endsWith("/company")) return "Профиль компании";
    if (pathname.endsWith("/reps")) return "Представители";
    if (pathname.endsWith("/products")) return "Товары";
    if (pathname.endsWith("/qr")) return "QR-коды";
    if (pathname.endsWith("/leads")) return "Вас сканировали";
    if (pathname.includes("/leads/")) return "Карточка лида";
    if (pathname.endsWith("/you-scanned") || pathname.includes("/you-scanned/")) return "Вы сканировали";
    if (pathname.endsWith("/analytics")) return "Аналитика интереса";
    if (pathname.endsWith("/documents")) return "Документы";
    return "Мой стенд";
  }
  if (pathname === "/") return "Обзор";
  if (pathname === "/events") return "Мероприятия";
  if (pathname === "/event-groups") return "Группы выставок";
  if (pathname === "/support") return "Чат поддержки";
  if (pathname === "/support-bot") return "Чат-бот";
  if (pathname === "/ui-strings") return "Системные тексты";
  if (currentEventId) {
    if (pathname.endsWith("/exhibitors")) return "Экспоненты";
    if (pathname.endsWith("/access")) return "Доступы";
    if (pathname.endsWith("/accreditation")) return "Аккредитация";
    if (pathname.endsWith("/map")) return "Карта";
    if (pathname.endsWith("/schedule")) return "Программа";
    if (pathname.endsWith("/support-bot") || pathname === "/support-bot") return "Чат-бот";
    if (pathname === "/ui-strings" || pathname.endsWith("/ui-strings")) return "Системные тексты";
    if (pathname === "/settings" || pathname.endsWith("/settings")) return "Настройки";
    if (pathname.endsWith("/support")) return "Поддержка";
    if (pathname.endsWith("/stats")) return "Аналитика";
    if (/\/exhibitors\/[^/]+\/(leads|you-scanned)\//.test(pathname)) return "Карточка лида";
    if (/\/exhibitors\/[^/]+\/you-scanned$/.test(pathname)) return "Вы сканировали";
    if (/\/exhibitors\/[^/]+\/leads$/.test(pathname)) return "Вас сканировали";
    if (/\/exhibitors\/[^/]+$/.test(pathname)) return "Экспонент";
    return "Мероприятие";
  }
  return "MVK One";
}

// ─── Auth guard ────────────────────────────────────────────────────────────
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAdminAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (!isLoading && !user && pathname !== "/login") {
      navigate("/login", { replace: true });
      return;
    }
    // Exhibitor cabinet is analytics-only under /exhibitor/*
    if (!isLoading && user?.role === "exhibitor" && pathname !== "/login") {
      const onExhibitorArea =
        pathname === "/exhibitor" || pathname.startsWith("/exhibitor/");
      if (!onExhibitorArea) {
        navigate("/exhibitor", { replace: true });
      }
    }
    // Curator (multi-event organizer): home is the events list, not a single show.
    if (!isLoading && user?.role === "curator" && pathname === "/") {
      navigate("/events", { replace: true });
    }
  }, [user, isLoading, pathname, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user && pathname !== "/login") return null;
  return <>{children}</>;
}

// ─── Nav item types ────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  exact?: boolean;
}

// ─── Sidebar Content ───────────────────────────────────────────────────────
function SidebarContent({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate: (href: string) => void;
}) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentEventId = useCurrentEventId();
  const { event: currentEvent } = useAdminEvent(currentEventId ?? undefined);

  const isActive = (href: string, exact = false) => {
    const p = location.pathname;
    if (exact) return p === href;
    if (p === href) return true;
    return p.startsWith(`${href}/`);
  };

  // Nav items per role
  let topNav: NavItem[] = [];
  let contextNav: NavItem[] = [];

  if (user?.role === "super_admin") {
    topNav = [
      { label: "Обзор", icon: <LayoutDashboard size={18} />, href: "/", exact: true },
      { label: "Мероприятия", icon: <CalendarDays size={18} />, href: "/events", exact: true },
      { label: "Группы выставок", icon: <Layers size={18} />, href: "/event-groups" },
      { label: "Чат поддержки", icon: <Headset size={18} />, href: "/support", exact: true },
      // Глобальные «Чат-бот» / «Системные тексты» скрыты — чат-бот настраивается на выставку.
    ];
    if (currentEventId) {
      contextNav = [
        { label: "Экспоненты", icon: <Building2 size={18} />, href: `/events/${currentEventId}/exhibitors` },
        { label: "Доступы", icon: <Users size={18} />, href: `/events/${currentEventId}/access` },
        { label: "Карта / стенды", icon: <Map size={18} />, href: `/events/${currentEventId}/map` },
        { label: "Программа", icon: <CalendarCheck size={18} />, href: `/events/${currentEventId}/schedule` },
        { label: "Чат-бот", icon: <Bot size={18} />, href: `/events/${currentEventId}/support-bot` },
        { label: "Поддержка выставки", icon: <Headset size={18} />, href: `/events/${currentEventId}/support` },
        { label: "Аналитика", icon: <BarChart3 size={18} />, href: `/events/${currentEventId}/stats` },
      ];
    }
  } else if (user?.role === "curator") {
    topNav = [
      { label: "Мероприятия", icon: <CalendarDays size={18} />, href: "/events", exact: true },
      { label: "Чат поддержки", icon: <Headset size={18} />, href: "/support", exact: true },
    ];
    if (currentEventId) {
      contextNav = [
        { label: "Обзор", icon: <Home size={18} />, href: `/events/${currentEventId}`, exact: true },
        { label: "Экспоненты", icon: <Building2 size={18} />, href: `/events/${currentEventId}/exhibitors` },
        { label: "Доступы", icon: <Users size={18} />, href: `/events/${currentEventId}/access` },
        { label: "Аккредитация", icon: <UserCheck size={18} />, href: `/events/${currentEventId}/accreditation` },
        { label: "Карта / стенды", icon: <Map size={18} />, href: `/events/${currentEventId}/map` },
        { label: "Программа", icon: <CalendarCheck size={18} />, href: `/events/${currentEventId}/schedule` },
        { label: "Чат-бот", icon: <Bot size={18} />, href: `/events/${currentEventId}/support-bot` },
        { label: "Поддержка выставки", icon: <Headset size={18} />, href: `/events/${currentEventId}/support` },
        { label: "Аналитика", icon: <BarChart3 size={18} />, href: `/events/${currentEventId}/stats` },
      ];
    }
  } else if (user?.role === "exhibitor") {
    topNav = [
      { label: "Мой стенд", icon: <LayoutDashboard size={18} />, href: "/exhibitor", exact: true },
      { label: "Профиль компании", icon: <Building2 size={18} />, href: "/exhibitor/company" },
      { label: "Товары на выставке", icon: <Tag size={18} />, href: "/exhibitor/products" },
      { label: "Представители", icon: <Users size={18} />, href: "/exhibitor/reps" },
      { label: "QR-коды", icon: <QrCode size={18} />, href: "/exhibitor/qr" },
      { label: "Вас сканировали", icon: <TrendingUp size={18} />, href: "/exhibitor/leads" },
      { label: "Вы сканировали", icon: <ScanLine size={18} />, href: "/exhibitor/you-scanned" },
      { label: "Аналитика", icon: <BarChart3 size={18} />, href: "/exhibitor/analytics" },
      { label: "Документы", icon: <FileText size={18} />, href: "/exhibitor/documents" },
    ];
  }

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-2.5 px-4 py-4 border-b border-gray-100 flex-shrink-0 ${collapsed ? "justify-center" : ""}`}>
        <img
          src="/icons/app-icon.png?v=20260916"
          alt="MVK One"
          width={32}
          height={32}
          className="h-8 w-8 rounded-lg flex-shrink-0 shadow-sm"
        />
        {!collapsed && (
          <div className="min-w-0">
            <div className="text-[13px] text-gray-900 leading-tight truncate">MVK One</div>
            <div className="text-[11px] text-gray-400 truncate">{user ? getRoleLabel(user.role) : ""}</div>
          </div>
        )}
      </div>

      {/* User badge */}
      {user && !collapsed && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-xl bg-gray-50 flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] flex-shrink-0 ${getRoleBadgeColor(user.role)}`}>
            {user.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-gray-900 truncate">{user.name}</div>
            <div className={`text-[10px] px-1.5 py-0.5 rounded-full inline-block mt-0.5 ${getRoleBadgeColor(user.role)}`}>
              {getRoleLabel(user.role)}
            </div>
          </div>
        </div>
      )}

      {/* Main nav */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-1 px-2">
        {topNav.map((item) => (
          <button
            key={item.href}
            onClick={() => onNavigate(item.href)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
              isActive(item.href, item.exact) ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            } ${collapsed ? "justify-center" : ""}`}
            title={collapsed ? item.label : undefined}
          >
            <span className="flex-shrink-0">{item.icon}</span>
            {!collapsed && <span className="text-[13px]">{item.label}</span>}
          </button>
        ))}

        {/* Event context for super admin / curator */}
        {(user?.role === "super_admin" || user?.role === "curator") && currentEvent && !collapsed && (
          <div className="pt-3 pb-1">
            <div className="px-3 mb-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Мероприятие</div>
              <button
                onClick={() => onNavigate(`/events/${currentEventId}`)}
                className="flex items-start gap-2 w-full group"
              >
                <div className="flex-1 text-left">
                  <div className="text-[12px] text-gray-900 leading-snug">{currentEvent.title}</div>
                  <div className="text-[11px] text-gray-400">{currentEvent.city}</div>
                </div>
                <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 mt-0.5" />
              </button>
            </div>
            <div className="space-y-1">
            {contextNav.map((item) => (
              <button
                key={item.href}
                onClick={() => onNavigate(item.href)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                  isActive(item.href, item.exact) ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                <span className="text-[13px]">{item.label}</span>
              </button>
            ))}
            </div>
          </div>
        )}
      </nav>

      {/* Bottom */}
      <div className="border-t border-gray-100 py-3 px-2 space-y-0.5 flex-shrink-0">
        <button
          type="button"
          onClick={() =>
            onNavigate(user?.role === "exhibitor" ? "/exhibitor/documents" : "/settings")
          }
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors ${collapsed ? "justify-center" : ""}`}
          title={collapsed ? "Документы" : undefined}
        >
          <FileText size={16} />
          {!collapsed && <span className="text-[13px]">Документы</span>}
        </button>
        <button
          type="button"
          onClick={() => {
            const origin =
              (import.meta.env.VITE_PUBLIC_WEB_URL as string | undefined)?.replace(/\/+$/, "") ||
              "https://app.mvk.ru";
            window.open(origin, "_blank", "noopener,noreferrer");
          }}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors ${collapsed ? "justify-center" : ""}`}
          title={collapsed ? "Клиентский вид" : undefined}
        >
          <Smartphone size={16} />
          {!collapsed && <span className="text-[13px]">Клиентский вид</span>}
        </button>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors ${collapsed ? "justify-center" : ""}`}
          title={collapsed ? "Выйти" : undefined}
        >
          <Shield size={16} />
          {!collapsed && <span className="text-[13px]">Выйти из аккаунта</span>}
        </button>
      </div>
    </div>
  );
}

// ─── Mobile bottom nav per role ────────────────────────────────────────────
function useMobileBottomNav() {
  const { user } = useAdminAuth();
  const currentEventId = useCurrentEventId();

  if (user?.role === "exhibitor") {
    return [
      { label: "Стенд", icon: <LayoutDashboard size={20} />, href: "/exhibitor", exact: true },
      { label: "Профиль", icon: <Building2 size={20} />, href: "/exhibitor/company" },
      { label: "Товары", icon: <Tag size={20} />, href: "/exhibitor/products" },
      { label: "Команда", icon: <Users size={20} />, href: "/exhibitor/reps" },
      { label: "QR", icon: <QrCode size={20} />, href: "/exhibitor/qr" },
    ];
  }
  if (user?.role === "curator") {
    if (currentEventId) {
      return [
        { label: "События", icon: <CalendarDays size={20} />, href: "/events", exact: true },
        { label: "Обзор", icon: <LayoutDashboard size={20} />, href: `/events/${currentEventId}`, exact: true },
        { label: "Экспоненты", icon: <Building2 size={20} />, href: `/events/${currentEventId}/exhibitors` },
        { label: "Регистр.", icon: <UserCheck size={20} />, href: `/events/${currentEventId}/accreditation` },
        { label: "Аналитика", icon: <BarChart3 size={20} />, href: `/events/${currentEventId}/stats` },
      ];
    }
    return [
      { label: "События", icon: <CalendarDays size={20} />, href: "/events", exact: true },
    ];
  }
  // super_admin
  if (currentEventId) {
    return [
      { label: "Обзор", icon: <LayoutDashboard size={20} />, href: "/", exact: true },
      { label: "Экспоненты", icon: <Building2 size={20} />, href: `/events/${currentEventId}/exhibitors` },
      { label: "Карта", icon: <Map size={20} />, href: `/events/${currentEventId}/map` },
      { label: "Аналитика", icon: <BarChart3 size={20} />, href: `/events/${currentEventId}/stats` },
      { label: "События", icon: <CalendarDays size={20} />, href: "/events" },
    ];
  }
  return [
    { label: "Обзор", icon: <LayoutDashboard size={20} />, href: "/", exact: true },
    { label: "События", icon: <CalendarDays size={20} />, href: "/events" },
    { label: "Настройки", icon: <Settings size={20} />, href: "/settings" },
  ];
}

// ─── Inner layout (needs auth context) ────────────────────────────────────
function AdminLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAdminAuth();
  const currentEventId = useCurrentEventId();
  const pageLabel = usePageLabel(user?.role, currentEventId);
  const mobileBottomNav = useMobileBottomNav();
  const { event: currentEvent } = useAdminEvent(currentEventId ?? undefined);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const isMapPage = location.pathname.endsWith("/map");
  const isLoginPage = location.pathname === "/login";

  // Login page: no layout chrome
  if (isLoginPage) {
    return <Outlet />;
  }

  const handleNavigate = (href: string) => {
    navigate(href);
    setMobileSidebarOpen(false);
  };

  return (
    <AuthGuard>
      <div className="flex bg-gray-50" style={{ height: "100dvh", overflow: "hidden" }}>
        {/* Desktop Sidebar */}
        <aside
          className={`hidden md:flex flex-col bg-white border-r border-gray-100 transition-all duration-200 flex-shrink-0 relative ${collapsed ? "w-[56px]" : "w-[220px]"}`}
        >
          <SidebarContent collapsed={collapsed} onNavigate={handleNavigate} />
          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute -right-3 bottom-16 flex items-center justify-center w-6 h-6 rounded-full bg-white border border-gray-200 shadow-sm text-gray-400 hover:text-gray-600 z-10"
          >
            {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </button>
        </aside>

        {/* Mobile Sidebar overlay */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-[260px] bg-white shadow-xl">
              <SidebarContent collapsed={false} onNavigate={handleNavigate} />
            </aside>
          </div>
        )}

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top header */}
          <header className="h-12 bg-white border-b border-gray-100 flex items-center gap-2 px-3 sm:px-4 flex-shrink-0 z-10">
            <button onClick={() => setMobileSidebarOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500">
              <Menu size={18} />
            </button>
            {currentEventId && (
              <button onClick={() => navigate(-1)} className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <span className="md:hidden text-[14px] text-gray-900 truncate block">{pageLabel}</span>
              <div className="hidden md:flex items-center gap-1.5 text-[13px]">
                <span className="text-gray-400">MVK One</span>
                {currentEvent && (
                  <>
                    <span className="text-gray-300">/</span>
                    <button onClick={() => navigate(`/events/${currentEventId}`)} className="text-gray-500 hover:text-gray-800 truncate max-w-[180px]">
                      {currentEvent.title}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-200 w-48">
              <Search size={14} className="text-gray-400 flex-shrink-0" />
              <input placeholder="Поиск..." className="flex-1 text-[13px] bg-transparent outline-none text-gray-700 placeholder-gray-400" />
            </div>
            <AdminNotificationsBell />
            {user && (
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] flex-shrink-0 ${getRoleBadgeColor(user.role)}`}>
                {user.initials}
              </div>
            )}
          </header>

          {/* Content */}
          {isMapPage ? (
            <div className="flex-1 overflow-hidden flex flex-col"><Outlet /></div>
          ) : (
            <main className="flex-1 overflow-y-auto" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <Outlet />
              <div className="h-16 md:hidden" />
            </main>
          )}

          {/* Mobile bottom nav */}
          <nav
            className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-40 flex items-stretch"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {mobileBottomNav.map((item) => {
              const active = item.exact
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);
              return (
                <button
                  key={item.href}
                  onClick={() => navigate(item.href)}
                  className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors min-h-[56px] ${
                    active ? "text-indigo-600" : "text-gray-400"
                  }`}
                >
                  {item.icon}
                  <span className="text-[10px] leading-none">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </AuthGuard>
  );
}

// ─── Exported layout with provider ────────────────────────────────────────
export function AdminLayout() {
  return (
    <AdminAuthProvider>
      <AdminLayoutInner />
    </AdminAuthProvider>
  );
}
